import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Delete, Visibility } from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { ActivityResponse } from '@/api/bol';
import {
  formatLocation,
  formatExternalReferences,
  formatSpecialCharacteristics,
  renderBooleanChip,
  renderChip,
} from '@/utils/datagridFormatters';

interface ActivityColumnsProps {
  onDelete: (activity: ActivityResponse) => void;
  canDelete?: boolean;
  deleteTooltip?: string;
}

export const createActivityColumns = ({ onDelete, canDelete = true, deleteTooltip = '' }: ActivityColumnsProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'ID',
    flex: 1.6,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { id?: string }) => row.id || '-',
  },
  {
    field: 'activityName',
    headerName: 'Activity Name',
    flex: 1.3,
    minWidth: 180,
    valueGetter: (_value: unknown, row: { activityName?: string }) => row.activityName || '-',
  },
  {
    field: 'activityType',
    headerName: 'Activity Type',
    flex: 1,
    minWidth: 140,
    renderCell: (params) => renderChip('activityType', params.row.activityType, 'outlined'),
  },
  {
    field: 'executionStatus',
    headerName: 'Execution Status',
    flex: 1,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { executionStatus?: string }) => row.executionStatus || '-',
  },
  {
    field: 'sequenceNumber',
    headerName: 'Sequence',
    flex: 0.7,
    minWidth: 100,
    valueGetter: (_value: unknown, row: { sequenceNumber?: number }) => row.sequenceNumber ?? '-',
  },
  {
    field: 'startLocation',
    headerName: 'Start Location',
    flex: 1.1,
    minWidth: 160,
    valueGetter: (_value: unknown, row: { startLocation?: unknown }) => formatLocation(row.startLocation as {
      location?: {
        address?: {
          addressLocality?: string | null;
          postalCode?: string | null;
        } | null;
      } | null;
    }),
  },
  {
    field: 'endLocation',
    headerName: 'End Location',
    flex: 1.1,
    minWidth: 160,
    valueGetter: (_value: unknown, row: { endLocation?: unknown }) => formatLocation(row.endLocation as {
      location?: {
        address?: {
          addressLocality?: string | null;
          postalCode?: string | null;
        } | null;
      } | null;
    }),
  },
  {
    field: 'isStartingService',
    headerName: 'Starts Service',
    flex: 0.9,
    minWidth: 130,
    renderCell: (params) => renderBooleanChip(params.row.isStartingService),
  },
  {
    field: 'isEndingService',
    headerName: 'Ends Service',
    flex: 0.9,
    minWidth: 130,
    renderCell: (params) => renderBooleanChip(params.row.isEndingService),
  },
  {
    field: 'contactsCount',
    headerName: 'Contacts',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (_value: unknown, row: { contacts?: unknown[] }) => row.contacts?.length || 0,
  },
  {
    field: 'serviceRequestsCount',
    headerName: 'Service Requests',
    flex: 1,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { serviceRequests?: unknown[] }) => row.serviceRequests?.length || 0,
  },
  {
    field: 'externalReferences',
    headerName: 'External References',
    flex: 1.6,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { externalReferences?: Array<{ externalReferenceType?: string | null; reference?: string | null }> }) =>
      formatExternalReferences(row.externalReferences),
  },
  {
    field: 'specialCharacteristics',
    headerName: 'Special Characteristics',
    flex: 1.6,
    minWidth: 220,
    valueGetter: (
      _value: unknown,
      row: {
        specialCharacteristics?: Array<{
          specialCharacteristicType?: string | null;
          characteristicReference?: string | null;
          stringValue?: string | null;
          numericalValue?: { value?: number; unit?: string } | null;
        }>;
      }
    ) => formatSpecialCharacteristics(row.specialCharacteristics),
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 120,
    getActions: (params) => [
      <Tooltip key="view" title="View details">
        <IconButton
          component={Link}
          to={`/activities/${params.row.id as string}`}
          size="small"
          color="primary"
          aria-label="View Details"
        >
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>,
      <GridActionsCellItem
        key="delete"
        icon={
          <Tooltip title={!canDelete ? deleteTooltip : 'Delete'}>
            <span>
              <Delete color={!canDelete ? 'disabled' : 'error'} />
            </span>
          </Tooltip>
        }
        label="Delete"
        onClick={() => onDelete(params.row as ActivityResponse)}
        disabled={!canDelete}
        showInMenu={false}
      />,
    ],
  },
];

export const defaultActivityColumnVisibilityModel = {
  id: true,
  activityName: true,
  activityType: true,
  executionStatus: true,
  sequenceNumber: true,
  startLocation: true,
  endLocation: true,
  isStartingService: false,
  isEndingService: false,
  contactsCount: false,
  serviceRequestsCount: false,
  externalReferences: false,
  specialCharacteristics: false,
};
